import { UserFeedbackContext } from "contexts/UserFeedbackContext";
import { createContext, useContext, useEffect, useState } from "react";
import { useCommunitiesAPI } from "service/CommunitiesAPIService";

export const CommunityContext = createContext();

export const CommunityContextProvider = ({ id, community, ...props}) => {

    const service = useCommunitiesAPI();
    const { setAndShowSuccessMessage, setAndShowErrorMessage } = useContext(UserFeedbackContext);

    // State
    const [communityinst, setCommunity] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [updating, setUpdating] = useState(false);

    const getCommunity = (id) => {
        if(!id) {
            console.error("missing ID to get community");
            return;
        };
        setSubmitting(true);
        return new Promise((resolve, reject) => {
            service.fetchById(id).then((response) => {
                setCommunity(response.data);
                setSubmitting(false);
                resolve(response);
            }).catch((e) => {
                setSubmitting(false);
                reject(e);
            })
        });
    }

    const updateCommunity = (values) => {
        setUpdating(true);
        return new Promise((resolve, reject) => {
            service.update(communityinst.id, values).then((response) => {
                setUpdating(false);
                setAndShowSuccessMessage("success.api.save");
                resolve(response);
            }).catch((e) => {
                setUpdating(false);
                reject(e);
            })
        });
    }

    const activateCommunityManagement = (values) => {
        setUpdating(true);
        return new Promise((resolve, reject) => {
            service.activateManagement(communityinst.id, values).then((response) => {
                setUpdating(false);
                resolve(response);
            }).catch((e) => {
                setUpdating(false);
                reject(e);
            })
        });
    }

    const updateStatus = (status, activationDate) => {
        return new Promise((resolve, reject) => {
            service.updateStatus(communityinst.id, status, activationDate).then((response) => {
                setCommunity(response.data);
                resolve(response);
            }).catch((e) => {
                reject(e);
            })
        });
    }

    const createInstallation = (data) => {
        setSubmitting(true);
        return new Promise((resolve, reject) => {
            service.createInstallation(communityinst.id, data)
                .then((response) => {
                    setAndShowSuccessMessage("success.api.create");
                    setSubmitting(false);
                    resolve(response);
                })
                .catch((e) => {
                    setSubmitting(false);
                    reject(e);
                });
        });
    }

    const updateInstallation = (id, data) => {
        return new Promise((resolve, reject) => {
            service.updateInstallation(communityinst.id, id, data)
                .then((response) => {
                    setAndShowSuccessMessage("success.api.create");
                    resolve(response);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    }

    const configureMember = (installationId, clientId) => {
        return new Promise((resolve, reject) => {
            service.configureMember(communityinst.id, installationId, clientId)
                .then((response) => {
                    setAndShowSuccessMessage("success.api.create");
                    resolve(response);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    }

    const createPhysicalUnit = (data) => {
        return service.createPhysicalUnit(communityinst.id, data);
    }

    const createClient = (data) => {
        return service.createClient(communityinst.id, data);
    }

    const getInstallations = () => {
        return service.getInstallations(communityinst.id);
    }

    const getCommunityInstallations = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        return service.getInstallations(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getMembers(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getConsumerInstallationMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getConsumers(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getProducerInstallationMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getProducers(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getConsumerClientMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getConsumersMembers(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getProducerClientMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getProducerMembers(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const getAllCommunityMembers = (filters=[], page_size = 30, page = 1, sortColumns, callBack, errorCallBack) => {
        service.getCommunityMembersList(communityinst.id, filters, page_size, page, sortColumns, callBack, errorCallBack);
    }

    const sendRegistrationEmail = (memberId) => {
        return service.sendRegistrationEmail(communityinst.id, memberId);
    }

    const getCommunitiesTariffModels = () => {
        return service.getCommunityTariffModels();
    }

    const updateBannerImage = (image) => {
        const formData = new FormData();
        formData.append("banner_image", image);

        return service.updateBannerImage(communityinst.id, formData);
    }

    const deleteBannerImage = () => { 
        return service.deleteBannerImage(communityinst.id);
    }

    const energy = (period, date) => {
        return service.energy(communityinst.id, period, date);
    }

    const energySurplus = (period, date) => {
        return service.energySurplus(communityinst.id, period, date);
    }

    const createInstallationCommunity = (communityID, data) => {
        return service.createInstallation(communityID, data);
    }

    useEffect(() => {
        if (id) {
            getCommunity(id).catch((e) => {
                // eat the exception
            });
        } else if (community) {
            setCommunity(community);
        } else {
            setCommunity(null);
        }
    }, []);

    useEffect(() => {
        if (community) {
            setCommunity(community);
        } else {
            setCommunity(null);
        }
    }, [community]);

    return (
        <CommunityContext.Provider value={{
            community: communityinst, setCommunity, getCommunity, updateCommunity, activateCommunityManagement, updateStatus,
            submitting, updating,
            createMember: createInstallation, getInstallations,
            updateInstallation, configureMember, createPhysicalUnit, createClient, getMembers, sendRegistrationEmail,
            getConsumerInstallationMembers, getProducerInstallationMembers, getCommunityInstallations,
            getCommunitiesTariffModels,
            updateBannerImage, deleteBannerImage,
            energy, energySurplus, createInstallationCommunity, getConsumerClientMembers, getProducerClientMembers, getAllCommunityMembers
        }}>
            {props.children}
        </CommunityContext.Provider>
    )
}

export const useCommunityContext = () => {
    const context = useContext(CommunityContext);
    if (!context) {
        throw new Error("useCommunityContext must be used within a CommunityContextProvider");
    }
    return context;
}